/**
 * The Save dialog's write: init → PUT the bytes → finalize, against the
 * user-session routes (`POST /vault/documents/save/*`). Runs in the HOST —
 * for a plugin's save, from the user's own session after they confirmed, which
 * is what makes the confirmation a server-side fact rather than a promise the
 * bridge keeps.
 *
 * Refusals come back as `ApiError`s the dialog handles: 409 `NAME_EXISTS`
 * (read the colliding files with `vaultSaveConflictsOf`), 409
 * `REPLACE_TARGET_CHANGED`, 413 `REPLACE_TOO_LARGE`.
 */
import {
  api,
  ApiError,
  type VaultContinuedAccess,
  type VaultDocumentDto,
  type VaultSaveTargetDto,
} from '@/lib/api';

export interface SaveToDocumentsInput {
  holderId: string;
  /** A folder in Documents; `null` = the Holder root. */
  folderId: string | null;
  name: string;
  mimeType: string;
  bytes: Blob;
  /** The arche whose output this is (its provenance). Absent = the user's own save. */
  onBehalfOfArcheId?: string;
  onConflict: 'fail' | 'keep-both' | 'replace';
  replace?: { documentId: string; expectedRevision: string; keepPreviousVersion: boolean };
  /** The continued access the user chose for the arche. */
  access: VaultContinuedAccess;
}

export interface SaveToDocumentsResult {
  document: VaultDocumentDto;
  access: VaultContinuedAccess;
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const BUSY_RETRY_DELAY_MS = 400;

export async function saveToDocuments(input: SaveToDocumentsInput): Promise<SaveToDocumentsResult> {
  const buffer = await input.bytes.arrayBuffer();
  const checksumSha256 = await sha256Hex(buffer);
  const init = await api.initVaultSaveToDocuments({
    holderId: input.holderId,
    folderId: input.folderId,
    name: input.name,
    mimeType: input.mimeType,
    sizeBytes: buffer.byteLength,
    checksumSha256,
    ...(input.onBehalfOfArcheId ? { onBehalfOfArcheId: input.onBehalfOfArcheId } : {}),
    onConflict: input.onConflict,
    ...(input.replace ? { replace: input.replace } : {}),
    access: input.access,
  });

  // Browsers forbid setting Content-Length; the presign is bound to the body.
  const headers = Object.fromEntries(
    Object.entries(init.requiredHeaders ?? {}).filter(
      ([k]) => k.toLowerCase() !== 'content-length',
    ),
  );
  const put = await fetch(init.uploadUrl, { method: 'PUT', headers, body: buffer });
  if (!put.ok) throw new Error(`The upload didn’t complete (HTTP ${put.status}). Try again.`);

  const finalizeBody = {
    holderId: input.holderId,
    documentId: init.document.documentId,
    checksumSha256,
    ...(input.onBehalfOfArcheId ? { onBehalfOfArcheId: input.onBehalfOfArcheId } : {}),
    access: input.access,
    ...(init.kind === 'replace'
      ? {
          replace: {
            saveId: init.saveId,
            expectedRevision: input.replace!.expectedRevision,
            folderId: input.folderId,
            name: init.finalName,
            mimeType: input.mimeType,
            keepPreviousVersion: input.replace!.keepPreviousVersion,
          },
        }
      : {}),
  };
  try {
    return await api.finalizeVaultSaveToDocuments(finalizeBody);
  } catch (err) {
    // A busy document refused BEFORE anything changed, and the staged bytes
    // are kept for exactly this retry.
    if (err instanceof ApiError && err.code === 'DOCUMENT_BUSY') {
      await new Promise((resolve) => setTimeout(resolve, BUSY_RETRY_DELAY_MS));
      return api.finalizeVaultSaveToDocuments(finalizeBody);
    }
    throw err;
  }
}

/**
 * Save bytes an internal arche's page holds where the Save dialog chose, as
 * the user, recording the arche as the file's source (it keeps no access). A surface that saves a batch passes this as the
 * dialog's `writer`, keeps the folder the target names, and saves the rest of
 * the batch there with `onConflict: 'keep-both'`.
 */
export async function saveBytesToTarget(
  target: VaultSaveTargetDto,
  mimeType: string,
  bytes: Blob,
  /** The internal arche whose output this is — recorded as the file's source. */
  sourceArcheId: string,
): Promise<{ documentId: string; name: string }> {
  const { document } = await saveToDocuments({
    holderId: target.holderId,
    folderId: target.folderId,
    name: target.name,
    mimeType,
    bytes,
    onBehalfOfArcheId: sourceArcheId,
    onConflict: target.onConflict,
    ...(target.replace ? { replace: target.replace } : {}),
    access: 'none',
  });
  return { documentId: document.documentId, name: document.name };
}
